import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { createManualTransaction } from '@/services/accountService';
import { toast } from 'sonner';

interface ManualTransactionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  accountName: string;
  onSuccess: () => void;
}

export function ManualTransactionDialog({
  open,
  onOpenChange,
  accountId,
  accountName,
  onSuccess
}: ManualTransactionDialogProps) {
  const [transactionType, setTransactionType] = useState<'credit' | 'debit' | 'adjustment'>('credit');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      toast.error('يرجى إدخال مبلغ صحيح / Please enter a valid amount');
      return;
    }

    if (!description.trim()) {
      toast.error('يرجى إدخال وصف المعاملة / Please enter transaction description');
      return;
    }

    setLoading(true);
    try {
      await createManualTransaction(accountId, amountNum, transactionType, description);

      toast.success('تم إضافة المعاملة بنجاح / Transaction added successfully');
      onSuccess();
      onOpenChange(false);
      
      // Reset form
      setAmount('');
      setDescription('');
      setTransactionType('credit');
    } catch (error) {
      console.error('Error creating transaction:', error);
      toast.error('فشل إضافة المعاملة / Failed to add transaction');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            إضافة معاملة يدوية / Add Manual Transaction
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            الحساب / Account: {accountName}
          </p>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="transaction_type">نوع المعاملة / Transaction Type</Label>
            <Select 
              value={transactionType} 
              onValueChange={(value: 'credit' | 'debit' | 'adjustment') => setTransactionType(value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="credit">دائن - إضافة رصيد / Credit - Add Balance</SelectItem>
                <SelectItem value="debit">مدين - خصم رصيد / Debit - Subtract Balance</SelectItem>
                <SelectItem value="adjustment">تسوية - تعديل الرصيد / Adjustment - Set Balance</SelectItem>
              </SelectContent>
            </Select>
            {transactionType === 'adjustment' && (
              <p className="text-xs text-muted-foreground">
                التسوية تحدد الرصيد الجديد مباشرة / Adjustment sets the new balance directly
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">
              {transactionType === 'adjustment' ? 'الرصيد الجديد / New Balance' : 'المبلغ / Amount'} (EGP)
            </Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">الوصف / Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="مثال: إيداع نقدي / Example: Cash deposit"
              rows={3}
              required
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء / Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'جاري الحفظ... / Saving...' : 'حفظ / Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
